import type { RechercheResultDemandeReservationFondsDTO } from '@/features/api/extranet/dto/Demande/Recherche/ReservationFonds'
import type { DemandeCreateDTO, DemandeCreateResultDTO } from '@/features/api/extranet/dto/Demande/shared'
import type {
  DemandeCxpi,
  RechercheResultDossierReservationFondsDTO,
} from '@/features/api/extranet/dto/Dossier/Recherche/ReservationFonds'
// import { useAppStateStore } from '@/features/application'
import { demandeToLocation, locationToParams } from '@/features/application/locations'
import { TRANSITION_CANCELED } from '@/features/domaine/machine-etats'
import { estDrfValideEtNbContratsAtteints } from '@/features/domaine/regles-akto/CXPI'
import type { SousDispositifDRF } from '@/features/domaine/types/opco/Demande/CXPI'
import { useAjouterDrf } from '@/features/interface/demande/ajouter-drf'
import { useDialog } from '@/features/interface/dialog'
import { useFavorisStore } from '@/features/interface/favoris/favorisStore.ts'
import type { DRFRechercheModule, DRFRechercheResult } from '@/features/interface/recherches/drf/DRFType.ts'
import { SilentError } from '@/features/shared/types'

import * as api from '@/features/services/demande'
import { isNotNullOrUndefined } from '@/features/shared/tools'
import { navigateTo, type NavigateToDemande, type NavigateToDemandeExtranet, type ToModule } from '@/router/navigateTo'

export type DRFAction = 'show-drf' | 'edit-drf' | 'clone-drf' | 'delete-drf' | 'add-cxpi'
export type DRFActionEnable = { [Property in DRFAction]: boolean }

export function useDRFActions(typeDemande: DRFRechercheModule) {
  const { showDialog: showAjoute, showClone } = useAjouterDrf()
  const { showDialog } = useDialog()
  const { addFavori } = useFavorisStore()

  const storeRecherche = locationToParams({ to: 'recherche', domaine: 'drf', module: typeDemande }).useStore()

  async function addDemandeDRF() {
    try {
      const drf = await showAjoute()
      navigateTo({
        to: 'detail',
        domaine: 'drf',
        module: 'demande',
        dispositif: 'fpe',
        sousDispositif: 'sousDispositif' in drf ? drf.sousDispositif : null,
        id: drf.id,
      } as NavigateToDemande)
    } catch (err) {
      if (err !== TRANSITION_CANCELED) throw err
    }
  }

  async function deleteDemandeDRF(item: DRFRechercheResult) {
    if (estDossier(item) || item.niveauPermission !== 'Ecriture') return

    const result = await showDialog({
      message: 'Confirmez-vous la suppression du brouillon ?',
      type: 'YesNo',
      title: 'Supprimer un brouillon',
    })
    if (result === 'Yes') {
      const location = demandeToLocation({
        typeDemande: 'ReservationFonds',
        dispositif: item.dispositif,
        origine: 'extranet',
        sousDispositif: item.sousDispositif,
        id: item.id,
      }) as NavigateToDemandeExtranet

      await api.demandeService.deleteDemande(location)
      await storeRecherche.reloadData()
    }
  }

  async function cloneDemandeDRF(item: RechercheResultDemandeReservationFondsDTO) {
    if (item.niveauPermission !== 'Ecriture' || item.statut !== 'SaisieAdherent') return

    try {
      const drf = (await showClone({
        idDemande: item.id,
        siret: item.adherent.siret.valeur!,
        idcc: item.adherent.idcc?.code ?? '',
        sousDispositif: item.sousDispositif as SousDispositifDRF,
        permissions: item.niveauPermission,
      })) as Required<DemandeCreateResultDTO>

      navigateTo({
        to: 'detail',
        domaine: 'drf',
        module: 'demande',
        dispositif: 'fpe',
        sousDispositif: 'sousDispositif' in drf ? drf.sousDispositif : null,
        id: drf.id,
      } as NavigateToDemande)
    } catch (err) {
      if (err === TRANSITION_CANCELED) return
      if (err instanceof SilentError) return
      throw err
    }
  }

  function navigateToCXPI(item: DRFRechercheResult, cxpi: DemandeCxpi) {
    return navigateTo({
      to: 'detail',
      domaine: 'formations',
      module: 'demande',
      dispositif: 'fpe',
      sousDispositif: item.sousDispositif!.toLowerCase(),
      id: cxpi.demandeId!,
    } as NavigateToDemande)
  }

  function navigateToDRF(item: DRFRechercheResult) {
    const module: ToModule = estDemande(item) ? 'demande' : 'dossier'
    return navigateTo({
      to: 'detail',
      domaine: 'drf',
      module,
      dispositif: 'fpe',
      sousDispositif: item.sousDispositif!.toLowerCase(),
      code: item.code,
      id: item.id,
    } as NavigateToDemande)
  }

  function estDemande(item: DRFRechercheResult): item is RechercheResultDemandeReservationFondsDTO {
    return isNotNullOrUndefined(item.id)
  }

  function estDossier(item: DRFRechercheResult): item is RechercheResultDossierReservationFondsDTO {
    return 'code' in item && isNotNullOrUndefined(item.code)
  }

  function estAddCxpi(item: DRFRechercheResult): item is RechercheResultDossierReservationFondsDTO {
    return (
      estDossier(item) &&
      estDrfValideEtNbContratsAtteints(
        item.nombreContrats!,
        new Date(item.dateFinValiditeDrf!),
        item.statut,
        item.demandes!,
        item.dossiersLies!,
      )
    )
  }

  const getItemState = (item: DRFRechercheResult): DRFActionEnable => {
    const editDRF = estDemande(item) && item.statut === 'SaisieAdherent' && item.niveauPermission === 'Ecriture'
    const deleteDRF = editDRF
    const showDrf = !editDRF
    const cloneDRF = false //estDemande(item) && item.statut === 'SaisieAdherent' && item.niveauPermission === 'Ecriture'
    const addCXPI = estAddCxpi(item)

    return {
      'edit-drf': editDRF,
      'show-drf': showDrf,
      'clone-drf': cloneDRF,
      'delete-drf': deleteDRF,
      'add-cxpi': addCXPI,
    }
  }

  async function addDemandeCXPI(item: RechercheResultDossierReservationFondsDTO) {
    const createCxpiPayload: DemandeCreateDTO<'MODEL_CXPI'> = {
      typeDemande: 'Formation',
      idcc: item.idccEmployeur,
      codeDrf: item.code,
      dispositif: 'FPE',
      sousDispositif: item.sousDispositif as SousDispositifDRF,
      typePublic: 'Interimaire',
      siret: item.etablissementAdherentPrincipal!.siret!.valeur!,
      consentementTraitementDonnees: true,
    }

    const cxpi = await api.demandeService.addDemande(createCxpiPayload)
    navigateTo({
      to: 'detail',
      domaine: 'formations',
      module: 'demande',
      dispositif: 'fpe',
      sousDispositif: 'sousDispositif' in cxpi ? cxpi.sousDispositif : null,
      id: cxpi.id,
    } as NavigateToDemande)
  }

  return {
    addDemandeDRF,
    deleteDemandeDRF,
    cloneDemandeDRF,
    navigateToDRF,
    navigateToCXPI,
    addDemandeCXPI,
    addFavori,
    estAddCxpi,
    estDemande,
    estDossier,
    getItemState,
  }
}
